import { Box } from '@/components/ui/box';
import { Button } from '@/components/ui/button';
import { Chip } from '@/components/ui/chip';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/drodown-menu-custom';
import { Ellipsis } from '@/components/ui/ellipsis';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Kbd } from '@/components/ui/kbd';
import { Text } from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  useGetAbstractScreeningByIdService,
  useUpdatePaperScreeningResultJustificationService,
  useUpdatePaperScreeningResultService,
  useUpdatePaperScreeningValidationStatusService,
} from '@/services/literatureReviews';
import { InvertedIndexUtil } from '@ditto/shared';
import { GetAbstractScreeningResultsResponse } from '@ditto/shared';
import {
  Ban,
  BookOpen,
  Check,
  ChevronDown,
  Circle,
  CircleHelp,
  ExternalLink,
  Pencil,
  Stars,
} from 'lucide-react';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Link, useParams } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';

export const DetailedModal: FC<{
  paperScreening:
    | GetAbstractScreeningResultsResponse['data'][number]
    | undefined;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selectNext: () => void;
  selectPrevious: () => void;
  isFirst: boolean;
  isLast: boolean;
}> = ({
  paperScreening,
  isOpen,
  onOpenChange,
  selectNext,
  selectPrevious,
  isFirst,
  isLast,
}) => {
  const [status, setStatus] = useState<
    ('include' | 'exclude' | 'inconclusive') | undefined
  >(paperScreening?.inclusionStatus);

  const [justification, setJustification] = useState<string>(
    paperScreening?.justification ?? ''
  );

  const literatureReview_id = useParams().literatureReview_id;

  const { abstractScreening } =
    useGetAbstractScreeningByIdService(literatureReview_id);
  const allCriteria = abstractScreening?.criteria ?? [];

  const getAbstractInvertedIndexContent = (abstractInvertedIndex?: {
    [key: string]: number[];
  }) => {
    if (!abstractInvertedIndex) {
      return '';
    }

    // Use the simplified method that preserves formatting
    return InvertedIndexUtil.createAbstractFromInvertedIndex(
      abstractInvertedIndex
    );
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger navigation if user is typing in an input/textarea
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      if (event.key === 'p' && !isFirst) {
        selectPrevious();
      } else if (event.key === 'n' && !isLast) {
        selectNext();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFirst, isLast, selectPrevious, selectNext]);

  const paper = paperScreening?.work;
  if (!literatureReview_id) {
    return null;
  }
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1200px] min-w-[800px]">
        <DialogHeader className="gap-4 flex flex-row">
          <FlexRow
            alignItems="center"
            justifyContent="end"
            gap="2"
            w="full"
            mr="4"
          >
            <Button
              variant="secondary"
              size="sm"
              onClick={selectPrevious}
              disabled={isFirst}
            >
              <Kbd>P</Kbd>
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={selectNext}
              disabled={isLast}
            >
              <Kbd>N</Kbd>
              Next
            </Button>
          </FlexRow>
        </DialogHeader>
        <FlexRow w="full" h="full" gap="5">
          <FlexCol
            w="1/2"
            h="full"
            gap="6"
            className="bg-neutral-50 p-5 rounded-lg max-h-[70vh] overflow-y-auto"
          >
            <FlexCol gap="1">
              <Text variant="body-lg-bold" color="primary">
                {paper?.name}
              </Text>
              <Ellipsis lineClamp={3} hasTooltip asChild>
                <Text variant="body-sm" color="tertiary">
                  {paper?.authors}
                </Text>
              </Ellipsis>
              <FlexRow
                alignItems="center"
                justifyContent="start"
                className="gap-2"
              >
                <BookOpen className="w-4 h-4 text-tertiary" />
                <Text variant="body-sm" color="tertiary">
                  {paper?.journal},{' '}
                  {new Date(paper?.publicationDate ?? '').toLocaleDateString()}
                </Text>
              </FlexRow>
              {paper?.doi && (
                <FlexRow
                  alignItems="center"
                  justifyContent="start"
                  className="gap-2"
                >
                  <Link
                    to={paper.doi}
                    target="_blank"
                    onClick={e => e.stopPropagation()}
                  >
                    <FlexRow
                      alignItems="center"
                      justifyContent="start"
                      className="gap-2"
                    >
                      <Text variant="body-xs" className="text-blue-700">
                        View full article
                      </Text>
                      <ExternalLink className="w-4 h-4 text-blue-700" />
                    </FlexRow>
                  </Link>
                </FlexRow>
              )}
            </FlexCol>
            <div className="break-words">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({ children }) => (
                    <h1 className="text-2xl font-bold mb-4 text-gray-900">
                      {children}
                    </h1>
                  ),
                  h2: ({ children }) => (
                    <h2 className="text-xl font-semibold mb-3 text-gray-900">
                      {children}
                    </h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="text-lg font-medium mb-2 text-gray-900">
                      {children}
                    </h3>
                  ),
                  p: ({ children }) => (
                    <p className="mb-2 text-gray-700">{children}</p>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-bold">{children}</strong>
                  ),
                  em: ({ children }) => <em className="italic">{children}</em>,
                  sup: ({ children }) => (
                    <sup className="text-xs">{children}</sup>
                  ),
                }}
              >
                {getAbstractInvertedIndexContent(paper?.abstractInvertedIndex)}
              </ReactMarkdown>
            </div>
          </FlexCol>
          <FlexCol
            w="1/2"
            h="full"
            gap="6"
            className="max-h-[70vh] overflow-y-auto"
          >
            <ScreeningDecisionBlock
              paperScreening={paperScreening}
              status={status}
              justification={justification}
              setStatus={setStatus}
              setJustification={setJustification}
            />
            <FlexCol gap="3">
              <Text variant="body" color="primary">
                Exclusion criteria
              </Text>
              {allCriteria.map(criteria => (
                <CriteriaBlock
                  key={criteria.id}
                  criteria={criteria}
                  paperScreening={paperScreening}
                />
              ))}
            </FlexCol>
          </FlexCol>
        </FlexRow>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ScreeningDecisionBlock: FC<{
  paperScreening:
    | GetAbstractScreeningResultsResponse['data'][number]
    | undefined;
  status: 'include' | 'exclude' | 'inconclusive' | undefined;
  justification: string;
  setStatus: (
    status: 'include' | 'exclude' | 'inconclusive' | undefined
  ) => void;
  setJustification: (justification: string) => void;
}> = ({
  paperScreening,
  status,
  justification,
  setStatus,
  setJustification,
}) => {
  const { updatePaperScreeningResult } = useUpdatePaperScreeningResultService();

  const { updatePaperScreeningValidationStatus } =
    useUpdatePaperScreeningValidationStatusService();

  const { updatePaperScreeningResultJustification } =
    useUpdatePaperScreeningResultJustificationService();

  const [validationStatus, setValidationStatus] = useState<
    'not_validated' | 'validated'
  >(paperScreening?.validationStatus ?? 'not_validated');

  const handleUpdatePaperScreeningResult =
    (status: 'include' | 'exclude') => () => {
      if (validationStatus === 'validated') {
        return;
      }
      updatePaperScreeningResult({
        paperScreeningId: paperScreening?.id ?? '',
        data: { screeningResult: status },
      });
      setStatus(status);
    };

  const handleUpdatePaperScreeningValidationStatus =
    (validationStatus: 'not_validated' | 'validated') => () => {
      updatePaperScreeningValidationStatus({
        paperScreeningId: paperScreening?.id ?? '',
        data: { validationStatus },
      });
      setValidationStatus(validationStatus);
    };

  let background: string, textColor: string, iconColor: string, icon;
  if (status === 'include') {
    background = 'bg-green-50';
    textColor = 'text-green-800';
    iconColor = 'text-green-600';
    icon = <Check className="w-4 h-4" />;
  } else if (status === 'exclude') {
    background = 'bg-red-100';
    textColor = 'text-red-800';
    iconColor = 'text-red-600';
    icon = <Ban className="w-4 h-4" />;
  } else if (status === 'inconclusive') {
    background = 'bg-yellow-100';
    textColor = 'text-yellow-900';
    iconColor = 'text-yellow-700';
    icon = <CircleHelp className="w-4 h-4" />;
  } else {
    background = 'bg-gray-100';
    textColor = 'text-gray-800';
    iconColor = 'text-gray-600';
  }

  const saveJustification = () => {
    if (!justification) {
      setJustification(justification);
      toast.error('Justification is required');
      return;
    }
    updatePaperScreeningResultJustification(
      {
        paperScreeningId: paperScreening?.id ?? '',
        data: { justification },
      },
      {
        onSuccess: () => {
          toast.success('Justification saved');
        },
        onError: () => {
          setJustification(justification);
          toast.error('Failed to save justification');
        },
      }
    );
  };

  const handleUpdatePaperScreeningResultJustification = (
    e: ChangeEvent<HTMLTextAreaElement>
  ) => {
    setJustification(e.target.value);
  };

  const disabledValidation = status === 'inconclusive' || status === undefined;

  const disabledValidationReason =
    status === 'inconclusive'
      ? 'You cannot validate a paper screening if the status is inconclusive'
      : status === undefined
        ? 'You cannot validate a paper screening if the status is not set'
        : !paperScreening?.justification?.trim()
          ? 'You cannot validate a paper screening if the justification is not set'
          : undefined;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore shortcut if focused on input, textarea, or contentEditable element
      const el = event.target as HTMLElement;
      if (
        el.tagName === 'INPUT' ||
        el.tagName === 'TEXTAREA' ||
        el.isContentEditable
      ) {
        return;
      }

      if (event.key === 'v' && !disabledValidation) {
        handleUpdatePaperScreeningValidationStatus('validated')();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleUpdatePaperScreeningValidationStatus, disabledValidation]);

  return (
    <FlexCol gap="2" className="bg-neutral-50 p-5 rounded-lg">
      <Text variant="body" color="primary">
        Screening decision
      </Text>
      <FlexCol
        gap="2"
        className="bg-white p-5 rounded-lg border border-neutral-100"
      >
        <FlexCol gap="3">
          <DropdownMenu>
            <DropdownMenuTrigger
              disabled={validationStatus === 'validated'}
              className="w-fit"
            >
              <FlexRow
                alignItems="center"
                gap="2"
                justifyContent="center"
                className={cn(background, 'p-1.5 rounded-xl h-full ')}
              >
                {icon && <span className={iconColor}>{icon}</span>}
                <Text variant="body-sm" className={`capitalize ${textColor}`}>
                  {status ?? 'Set status'}
                </Text>
                {validationStatus === 'not_validated' && <ChevronDown />}
              </FlexRow>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                onClick={handleUpdatePaperScreeningResult('include')}
              >
                Include
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleUpdatePaperScreeningResult('exclude')}
              >
                Exclude
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <FlexCol gap="1">
            <FlexRow alignItems="center" gap="2">
              <Stars className="w-4 h-4 text-tertiary" />
              <Text variant="caption-bold" color="tertiary">
                AI rationale:
              </Text>
            </FlexRow>
            {validationStatus === 'validated' ? (
              <Text variant="body-sm" color="primary">
                {justification}
              </Text>
            ) : (
              <Textarea
                value={justification}
                onBlur={saveJustification}
                onChange={handleUpdatePaperScreeningResultJustification}
                className="bg-neutral-50"
              />
            )}
          </FlexCol>
          {validationStatus === 'validated' ? (
            <Button
              variant="outline"
              onClick={handleUpdatePaperScreeningValidationStatus(
                'not_validated'
              )}
            >
              <Pencil className="w-4 h-4" />
              Edit
            </Button>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Button
                    disabled={disabledValidation}
                    onClick={handleUpdatePaperScreeningValidationStatus(
                      'validated'
                    )}
                    className="w-full"
                  >
                    <Check className="w-4 h-4" />
                    Validate [V]
                  </Button>
                </div>
              </TooltipTrigger>
              {disabledValidation && (
                <TooltipContent>{disabledValidationReason}</TooltipContent>
              )}
            </Tooltip>
          )}
        </FlexCol>
      </FlexCol>
    </FlexCol>
  );
};

const CriteriaBlock: FC<{
  criteria: { id: string; name: string; instructions: string };
  paperScreening:
    | GetAbstractScreeningResultsResponse['data'][number]
    | undefined;
}> = ({ criteria, paperScreening }) => {
  const result = paperScreening?.criteriaResults.find(
    cr => cr.criteriaId === criteria.id
  );
  const getColor = (value: 'yes' | 'no' | 'not_found') => {
    if (value === 'yes') {
      return 'fill-red-500 text-red-500';
    }
    if (value === 'no') {
      return 'fill-green-500 text-green-500';
    }
    return 'fill-yellow-500 text-yellow-500';
  };

  const getAiMotivation = (criteriaId: string) => {
    return (
      paperScreening?.criteriaResults.find(cr => cr.criteriaId === criteriaId)
        ?.motivation ?? ''
    );
  };

  const getCriteriaResult = (criteriaId: string) => {
    const result = paperScreening?.criteriaResults.find(
      cr => cr.criteriaId === criteriaId
    )?.value;
    if (result === 'yes') {
      return 'Yes';
    }
    if (result === 'no') {
      return 'No';
    }
    return 'Not found';
  };
  return (
    <FlexCol
      key={criteria.id}
      gap="2"
      className="border border-neutral-100 p-5 rounded-lg"
    >
      <FlexRow alignItems="center" gap="2">
        <Chip size="sm" interactive={false}>
          <Circle
            className={cn('w-2 h-2', getColor(result?.value ?? 'not_found'))}
          />

          <Text variant="body-xs" color="primary">
            {criteria.name}
          </Text>
        </Chip>
      </FlexRow>
      <FlexRow alignItems="center" gap="1" className="justify-between">
        <Text variant="body-sm" color="primary">
          {criteria.instructions}
        </Text>
        <Chip
          size="sm"
          interactive={false}
          className="bg-blue-50 text-blue-700 border border-blue-100"
        >
          <Chip.Label>
            {getCriteriaResult(criteria.id) ?? 'Not found'}
          </Chip.Label>
        </Chip>
      </FlexRow>
      {getAiMotivation(criteria.id) && (
        <Box className="bg-neutral-50 p-2 rounded-lg">
          <Text variant="caption-bold" color="tertiary">
            Explanation:
          </Text>
          <Text variant="body-sm" color="tertiary">
            {getAiMotivation(criteria.id)}
          </Text>
        </Box>
      )}
      {result?.quote && (
        <Box className="bg-neutral-50 p-2 rounded-lg">
          <Text variant="caption-bold" color="tertiary">
            Quote:
          </Text>
          <Text variant="body-sm" color="tertiary">
            {result.quote}
          </Text>
        </Box>
      )}
    </FlexCol>
  );
};
